import { memo, ReactNode, useCallback, useMemo, useState } from "react";
import { v1 } from "uuid";
import { Snackbar } from "./Snackbar";
import { SnackbarContext, SnackbarContextProps, SnackbarMessagePropsExt, SnackbarMessagesContext } from "./SnackbarContext";

export type SnackbarProviderProps = {
  children: ReactNode,
  onPush?: (message: SnackbarMessagePropsExt) => void
  onRemove?: (key?: string) => void
  onClear?: () => void
}

export const SnackbarProvider = memo<SnackbarProviderProps>(({ children, onPush, onRemove, onClear }) => {
  const messageStorage = useMemo(() => ({ messages: [] as SnackbarMessagePropsExt[] }), []);
  const [ messagesOnUI, setMessagesOnUI ] = useState<SnackbarMessagePropsExt[]>(messageStorage.messages);

  const push = useCallback((props: SnackbarMessagePropsExt) => {
    const existingIndex = messageStorage.messages.findIndex((item) => item.key === props.key);
    const index = existingIndex !== -1 ? existingIndex : messageStorage.messages.length;

    const newMessageProps: SnackbarMessagePropsExt = {
      ...messageStorage.messages[existingIndex],
      ...props,
      key: props.key || v1(),
      autoClose: props.autoClose === undefined
        ? props.type !== "error"
        : props.autoClose,
      autoCloseTimeout: props.autoCloseTimeout === undefined
        ? (props.type !== "error" ? 5000 : undefined)
        : props.autoCloseTimeout,
    };
    const newMessages = [
      ...messageStorage.messages.slice(0, index),
      newMessageProps,
      ...messageStorage.messages.slice(index + 1),
    ];

    Object.freeze(newMessageProps);
    Object.freeze(newMessages);

    messageStorage.messages = newMessages;
    setMessagesOnUI(newMessages);

    onPush?.(newMessageProps);

    return newMessageProps;
  }, [ messageStorage, setMessagesOnUI, onPush ]);

  const remove = useCallback(
    (key: string | undefined) => {
      const newMessages = messageStorage.messages.filter((props) => props.key !== key);
      Object.freeze(newMessages);
      messageStorage.messages = newMessages;
      setMessagesOnUI(newMessages);

      onRemove?.(key);
    },
    [ messageStorage, setMessagesOnUI, onRemove ],
  );

  const clear = useCallback(
    () => {
      const newMessages = [] as SnackbarMessagePropsExt[];
      Object.freeze(newMessages);
      messageStorage.messages = newMessages;
      setMessagesOnUI(newMessages);

      onClear?.();
    },
    [ messageStorage, setMessagesOnUI, onClear ],
  );

  const providerValue = useMemo(
    (): SnackbarContextProps => {
      return ({
        push,
        remove,
        clear,
      });
    },
    [ push, remove, clear ],
  );

  return (
    <SnackbarContext.Provider value={providerValue}>
      <SnackbarMessagesContext.Provider value={messagesOnUI}>
        {children}
        <Snackbar />
      </SnackbarMessagesContext.Provider>
    </SnackbarContext.Provider>
  );
});
